import { readStorage, writeStorage } from './storage';

/**
 * RFC 9562 UUID version 7: a 48-bit millisecond timestamp, then a 12-bit
 * counter, then random bits. Ids are strictly increasing within this page
 * (the counter breaks same-millisecond ties), so ordering outbox rows by id
 * replays a burst of edits to one record in the order they were made.
 */
let lastTimestamp = -1;
let counter = 0;

export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  let timestamp = Math.max(0, Math.floor(now), lastTimestamp);
  if (timestamp === lastTimestamp) {
    counter += 1;
    if (counter > 0xfff) {
      timestamp += 1; // counter exhausted: borrow the next millisecond
      counter = 0;
    }
  } else {
    counter = (bytes[6]! & 0x07) << 8; // random start, leaving headroom to count
  }
  lastTimestamp = timestamp;

  let remaining = timestamp;
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = remaining % 256;
    remaining = Math.floor(remaining / 256);
  }
  bytes[6] = 0x70 | ((counter >> 8) & 0x0f); // version 7 + counter high bits
  bytes[7] = counter & 0xff;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // RFC 4122 variant

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const DEVICE_KEY = 'kb.device_id';
let ephemeralDeviceId: string | null = null;

/**
 * Stable per-browser id sent with sync pushes. Not a secret and not an
 * identity: it only lets the server tell two devices of the same user apart.
 */
export function getDeviceId(): string {
  const stored = readStorage(DEVICE_KEY);
  if (stored) return stored;
  const id = ephemeralDeviceId ?? uuidv7();
  writeStorage(DEVICE_KEY, id);
  ephemeralDeviceId = id;
  return id;
}
